import { useMemo } from 'react';
import { getGamesByCategory, getLatestGames, type Game } from '@lib/content';
import { GAME_LIMITS } from '../config';

export interface GameRecommendations {
  sidebarGames: Game[];
  moreGames: Game[];
}

/**
 * Get series/version games for current game
 */
function getSeriesGames(currentGame: Game, allCategoryGames: Game[]): Game[] {
  const currentTitle = currentGame.title.toLowerCase();
  const currentSlug = currentGame.slug.toLowerCase();

  // Extract base name from title (remove numbers and common words)
  const getBaseName = (title: string) => {
    return title
      .toLowerCase()
      .replace(/\b(1|2|3|4|5|6|7|8|9|10|i+|ii+|iii+|iv+|v+)\b/g, '')
      .replace(/\b(epic|battle|new|super|mega|ultra|pro|plus|deluxe|winter|spooky|pool|party)\b/g, '')
      .replace(/[-:\s]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  };

  const getBaseSlug = (slug: string) => {
    return slug
      .replace(/-(\d+)$/, '') // Remove trailing numbers
      .replace(/-(epic|battle|new|super|mega|ultra|pro|plus|deluxe|winter|spooky|pool|party)(-\d+)?$/, '')
      .replace(/-(1|2|3|4|5|6|7|8|9|10)(-\w+)*$/, '');
  };

  const currentBaseName = getBaseName(currentGame.title);
  const currentBaseSlug = getBaseSlug(currentGame.slug);

  // Find series games
  const seriesGames = allCategoryGames.filter(game => {
    if (game.slug === currentGame.slug) return false;

    const gameBaseName = getBaseName(game.title);
    const gameBaseSlug = getBaseSlug(game.slug);

    // Check if same series by title or slug
    return (currentBaseName === gameBaseName && currentBaseName.length > 3) ||
      (currentBaseSlug === gameBaseSlug && currentBaseSlug.length > 3) ||
      // Manual series mappings for common cases
      isInSameSeries(currentGame.slug, game.slug);
  });

  // Sort by version/number (try to get sequential order)
  return seriesGames.sort((a, b) => {
    const aNum = extractNumber(a.title) || extractNumber(a.slug) || 0;
    const bNum = extractNumber(b.title) || extractNumber(b.slug) || 0;
    return aNum - bNum;
  });
}

/**
 * Extract number from title/slug for version ordering
 */
function extractNumber(text: string): number | null {
  const match = text.match(/\b(\d+)\b/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Manual series detection for common game series (based on actual games in project)
 */
function isInSameSeries(slug1: string, slug2: string): boolean {
  const seriesMap: Record<string, string[]> = {
    // Idle Restaurant / Business Series
    'idle-restaurants-tycoon': ['idle-startup-tycoon', 'hardware-tycoon', 'idle-lumber-inc', 'idle-toy-factories'],
    'idle-startup-tycoon': ['idle-restaurants-tycoon', 'hardware-tycoon', 'idle-lumber-inc', 'idle-toy-factories'],
    'hardware-tycoon': ['idle-restaurants-tycoon', 'idle-startup-tycoon', 'idle-lumber-inc', 'idle-toy-factories'],

    // Idle Mining Series
    'idle-mining-empire': ['idle-digging-tycoon', 'idle-gold-miner', 'idle-miner', 'idle-mining', 'doge-miner-2'],
    'idle-digging-tycoon': ['idle-mining-empire', 'idle-gold-miner', 'idle-miner', 'idle-mining', 'doge-miner-2'],
    'idle-gold-miner': ['idle-mining-empire', 'idle-digging-tycoon', 'idle-miner', 'idle-mining', 'doge-miner-2'],
    'idle-miner': ['idle-mining-empire', 'idle-digging-tycoon', 'idle-gold-miner', 'idle-mining', 'doge-miner-2'],
    'idle-mining': ['idle-mining-empire', 'idle-digging-tycoon', 'idle-gold-miner', 'idle-miner', 'doge-miner-2'],
    'doge-miner-2': ['idle-mining-empire', 'idle-digging-tycoon', 'idle-gold-miner', 'idle-miner', 'idle-mining'],

    // Idle Factory Series
    'idle-fill-factory-events': ['idle-bee-factory', 'idle-lumber-inc', 'idle-toy-factories'],
    'idle-bee-factory': ['idle-fill-factory-events', 'idle-lumber-inc', 'idle-toy-factories'],
    'idle-lumber-inc': ['idle-fill-factory-events', 'idle-bee-factory', 'idle-toy-factories'],
    'idle-toy-factories': ['idle-fill-factory-events', 'idle-bee-factory', 'idle-lumber-inc'],

    // Idle City / Development Series
    'idle-idle-game-dev': ['idle-light-city', 'idle-tree-city', 'idle-success', 'ltf-idle'],
    'idle-light-city': ['idle-idle-game-dev', 'idle-tree-city', 'idle-success', 'ltf-idle'],
    'idle-tree-city': ['idle-idle-game-dev', 'idle-light-city', 'idle-success', 'ltf-idle'],
    'idle-success': ['idle-idle-game-dev', 'idle-light-city', 'idle-tree-city', 'ltf-idle'],
    'ltf-idle': ['idle-idle-game-dev', 'idle-light-city', 'idle-tree-city', 'idle-success'],

    // General Idle Series
    'idle-zoo-safari-rescue': ['idle-ants', 'idle-shark', 'revolution-idle', 'idle-dice'],
    'idle-ants': ['idle-zoo-safari-rescue', 'idle-shark', 'revolution-idle', 'idle-dice'],
    'idle-shark': ['idle-zoo-safari-rescue', 'idle-ants', 'revolution-idle', 'idle-dice'],
    'revolution-idle': ['idle-zoo-safari-rescue', 'idle-ants', 'idle-shark', 'idle-dice'],
    'idle-dice': ['idle-zoo-safari-rescue', 'idle-ants', 'idle-shark', 'revolution-idle'],
    'idle-breakout': ['idle-success', 'revolution-idle', 'ltf-idle'],

    // Clicker Series
    'money-clicker': ['bitcoin-clicker', 'robuxclicker', 'clicker-heroes', 'cookie-master'],
    'bitcoin-clicker': ['money-clicker', 'robuxclicker', 'clicker-heroes', 'cookie-master'],
    'robuxclicker': ['money-clicker', 'bitcoin-clicker', 'clicker-heroes', 'cookie-master'],
    'clicker-heroes': ['money-clicker', 'bitcoin-clicker', 'robuxclicker', 'cookie-master'],

    // Cookie / Food Clicker Series
    'dalgona-candy-honeycomb-cookie': ['cookie-master', 'burger-clicker', 'poop-clicker', 'capybara-clicker'],
    'cookie-master': ['dalgona-candy-honeycomb-cookie', 'burger-clicker', 'money-clicker', 'clicker-heroes'],
    'burger-clicker': ['dalgona-candy-honeycomb-cookie', 'cookie-master', 'money-clicker', 'capybara-clicker'],

    // Capybara / Animal Clicker Series
    'capybara-clicker-2': ['capybara-clicker', 'cat-clicker-re', 'doggo-clicker', 'kiwi-clicker'],
    'capybara-clicker': ['capybara-clicker-2', 'cat-clicker-re', 'doggo-clicker', 'kiwi-clicker'],
    'cat-clicker-re': ['capybara-clicker-2', 'capybara-clicker', 'doggo-clicker', 'kiwi-clicker'],
    'doggo-clicker': ['capybara-clicker-2', 'capybara-clicker', 'cat-clicker-re', 'kiwi-clicker'],
    'kiwi-clicker': ['capybara-clicker-2', 'capybara-clicker', 'cat-clicker-re', 'doggo-clicker'],

    // Meme / Casual Clicker Series
    'italian-brainrot-clicker': ['poop-clicker-3', 'poop-clicker-flash', 'space-bar-clicker', 'blobby-clicker'],
    'poop-clicker-3': ['italian-brainrot-clicker', 'poop-clicker-flash', 'poop-clicker', 'tube-clicker'],
    'poop-clicker-flash': ['italian-brainrot-clicker', 'poop-clicker-3', 'poop-clicker', 'tube-clicker'],
    'poop-clicker': ['poop-clicker-3', 'poop-clicker-flash', 'tube-clicker', 'blobby-clicker'],
    'tube-clicker': ['poop-clicker-3', 'poop-clicker-flash', 'poop-clicker', 'blobby-clicker'],
    'space-bar-clicker': ['italian-brainrot-clicker', 'blobby-clicker', 'particle-clicker', 'poop-clicker'],
    'blobby-clicker': ['italian-brainrot-clicker', 'space-bar-clicker', 'particle-clicker', 'poop-clicker'],
    'particle-clicker': ['space-bar-clicker', 'blobby-clicker', 'clicker-heroes', 'money-clicker'],

    // Case / Game Clicker Series
    'case-clicker': ['csgo-clicker', 'minecraft-case-simulator', 'robuxclicker'],
    'csgo-clicker': ['case-clicker', 'minecraft-case-simulator', 'robuxclicker'],
    'minecraft-case-simulator': ['case-clicker', 'csgo-clicker', 'robuxclicker'],

    // Farm Series
    'andys-apple-farm': ['farm-match-seasons', 'chicky-farm', 'orb-farm', 'farm-clash-3d'],
    'farm-match-seasons': ['andys-apple-farm', 'chicky-farm', 'orb-farm', 'farm-clash-3d'],
    'chicky-farm': ['andys-apple-farm', 'farm-match-seasons', 'orb-farm', 'farm-clash-3d'],
    'orb-farm': ['andys-apple-farm', 'farm-match-seasons', 'chicky-farm', 'farm-clash-3d'],
    'farm-clash-3d': ['andys-apple-farm', 'farm-match-seasons', 'chicky-farm', 'orb-farm'],

    // Animal Simulator Series
    'dog-simulator-3d': ['cat-clicker-re', 'fox-simulator-3d', 'horse-simulator-3d', 'panda-simulator-3d', 'tiger-simulator-3d'],
    'fox-simulator-3d': ['dog-simulator-3d', 'horse-simulator-3d', 'panda-simulator-3d', 'tiger-simulator-3d', 'deer-simulator'],
    'horse-simulator-3d': ['dog-simulator-3d', 'fox-simulator-3d', 'panda-simulator-3d', 'tiger-simulator-3d', 'deer-simulator'],
    'panda-simulator-3d': ['dog-simulator-3d', 'fox-simulator-3d', 'horse-simulator-3d', 'tiger-simulator-3d', 'parrot-simulator'],
    'tiger-simulator-3d': ['dog-simulator-3d', 'fox-simulator-3d', 'horse-simulator-3d', 'panda-simulator-3d', 'deer-simulator'],
    'deer-simulator': ['dog-simulator-3d', 'fox-simulator-3d', 'horse-simulator-3d', 'tiger-simulator-3d', 'parrot-simulator'],
    'parrot-simulator': ['panda-simulator-3d', 'deer-simulator', 'dog-simulator-3d', 'fox-simulator-3d'],

    // Animal / Creature Simulator Series
    'christmas-deer-simulator': ['deer-simulator', 'crazy-pig-simulator', 'dog-simulator-3d', 'fox-simulator-3d'],
    'crazy-pig-simulator': ['christmas-deer-simulator', 'deer-simulator', 'dog-simulator-3d', 'nut-simulator'],
    'dragon-simulator-3d': ['tiger-simulator-3d', 'panda-simulator-3d', 'dog-simulator-3d', 'fox-simulator-3d'],
    'nut-simulator': ['crazy-pig-simulator', 'deer-simulator', 'parrot-simulator', 'dog-simulator-3d'],

    // Car Simulator Series
    'cars-simulator': ['3d-car-simulator', 'car-simulator-arena', 'edys-car-simulator', 'extreme-car-driving-simulator'],
    '3d-car-simulator': ['cars-simulator', 'car-simulator-arena', 'edys-car-simulator', 'extreme-car-driving-simulator'],
    'car-simulator-arena': ['cars-simulator', '3d-car-simulator', 'edys-car-simulator', 'extreme-car-driving-simulator'],
    'edys-car-simulator': ['cars-simulator', '3d-car-simulator', 'car-simulator-arena', 'extreme-car-driving-simulator'],
    'extreme-car-driving-simulator': ['cars-simulator', '3d-car-simulator', 'car-simulator-arena', 'edys-car-simulator', 'flying-car-simulator'],
    'flying-car-simulator': ['extreme-car-driving-simulator', '3d-car-simulator', 'cars-simulator', 'car-simulator-arena'],

    // Truck / Cargo Simulator Series
    '18-wheeler-cargo-simulator-2': ['18-wheeler-cargo-simulator', 'indian-truck-simulator', 'indian-truck-simulator3-d', 'off-road-rain-cargo-simulator'],
    '18-wheeler-cargo-simulator': ['18-wheeler-cargo-simulator-2', 'indian-truck-simulator', 'indian-truck-simulator3-d', 'off-road-rain-cargo-simulator'],
    'indian-truck-simulator': ['18-wheeler-cargo-simulator-2', '18-wheeler-cargo-simulator', 'indian-truck-simulator3-d', 'off-road-rain-cargo-simulator'],
    'indian-truck-simulator3-d': ['18-wheeler-cargo-simulator-2', '18-wheeler-cargo-simulator', 'indian-truck-simulator', 'off-road-rain-cargo-simulator'],
    'off-road-rain-cargo-simulator': ['18-wheeler-cargo-simulator-2', '18-wheeler-cargo-simulator', 'indian-truck-simulator', 'indian-truck-simulator3-d'],
    'realconstructionexcavatorsimulator': ['indian-truck-simulator', '18-wheeler-cargo-simulator', 'off-road-rain-cargo-simulator'],

    // Motorcycle Simulator Series
    '3d-moto-simulator-2': ['highway-bike-simulator', 'indian-uphill-bus-simulator-3d', 'cars-simulator'],
    'highway-bike-simulator': ['3d-moto-simulator-2', 'cars-simulator', '3d-car-simulator'],

    // Monster Truck Series
    'real-simulator-monster-truck': ['cars-simulator', '3d-car-simulator', 'car-simulator-arena', 'ultimate-offroad-simulator'],
    'ultimate-offroad-simulator': ['real-simulator-monster-truck', 'cars-simulator', '3d-car-simulator', 'off-road-rain-cargo-simulator'],

    // Battle / Crime Simulator Series
    'funny-battle-simulator': ['funny-battle-simulator-2', 'squid-battle-simulator', 'miami-crime-simulator-3d'],
    'funny-battle-simulator-2': ['funny-battle-simulator', 'squid-battle-simulator', 'miami-crime-simulator-3d'],
    'squid-battle-simulator': ['funny-battle-simulator', 'funny-battle-simulator-2', 'miami-crime-simulator-3d'],
    'miami-crime-simulator-3d': ['funny-battle-simulator', 'funny-battle-simulator-2', 'squid-battle-simulator', 'grand-vegas-simulator'],
    'grand-vegas-simulator': ['miami-crime-simulator-3d', 'funny-battle-simulator', 'yandere-simulator', 'president-simulator'],

    // Character / Life Simulator Series
    'bitlife-life-simulator-game': ['president-simulator', 'yandere-simulator', 'grand-vegas-simulator'],
    'president-simulator': ['bitlife-life-simulator-game', 'yandere-simulator', 'grand-vegas-simulator'],
    'yandere-simulator': ['bitlife-life-simulator-game', 'president-simulator', 'grand-vegas-simulator'],

    // Raccoon / Adventure Simulator Series
    'raccoon-adventure-city-simulator-3d': ['dog-simulator-3d', 'fox-simulator-3d', 'grand-vegas-simulator'],

    // Pizzeria / Restaurant Simulator Series
    'fnaf-pizzeria-simulator': ['idle-restaurants-tycoon', 'idle-startup-tycoon', 'hardware-tycoon'],

    // Other Simulation Games
    'webgl-fluid-simulation': ['badtimesimulator', 'realconstructionexcavatorsimulator', 'funny-battle-simulator'],
    'badtimesimulator': ['webgl-fluid-simulation', 'funny-battle-simulator', 'funny-battle-simulator-2'],

    // 3D Simulation Collection
    'indian-uphill-bus-simulator-3d': ['3d-moto-simulator-2', 'indian-truck-simulator', 'real-simulator-monster-truck'],
  };

  return seriesMap[slug1]?.includes(slug2) ?? false;
}

/**
 * Enhanced game recommendations with series/version priority
 * Priority: Featured games > Series games > Same category games > Latest games
 */
export function useGameRecommendations(game: Game | null): GameRecommendations {
  return useMemo(() => {
    if (!game) return { sidebarGames: [], moreGames: [] };

    // Define priority games for homepage sidebar based on new categories
    const priorityGames = [
      "money-clicker",
      "bitcoin-clicker",
      "capybara-clicker-2",
      "cat-clicker-re",
      "italian-brainrot-clicker",
      "space-bar-clicker",
      "blobby-clicker",
      "burger-clicker",
      "capybara-clicker",
      "clicker-heroes",
      "cookie-master",
      "csgo-clicker",
      "doggo-clicker",
      "kiwi-clicker",
      "particle-clicker"
    ];

    // Get all games from all categories of current game
    const allCategories = game.categories || [];
    const allRelatedGames: Game[] = [];
    const usedSlugs = new Set<string>([game.slug]);

    // Collect games from all categories (not just primary)
    for (const category of allCategories) {
      const categoryGames = getGamesByCategory(category).filter((g) => !usedSlugs.has(g.slug));
      allRelatedGames.push(...categoryGames);
      categoryGames.forEach(g => usedSlugs.add(g.slug));
    }

    // PRIORITY 1: Get series/version games first
    const seriesGames = getSeriesGames(game, allRelatedGames);

    // PRIORITY 2: Get priority games from the current game's categories
    const priorityRelatedGames = allRelatedGames.filter(g =>
      priorityGames.includes(g.slug) && !seriesGames.some(sg => sg.slug === g.slug)
    ).sort((a, b) => {
      const aIndex = priorityGames.indexOf(a.slug);
      const bIndex = priorityGames.indexOf(b.slug);
      return aIndex - bIndex;
    });

    // PRIORITY 3: Get remaining category games (excluding series and priority games)
    const remainingCategoryGames = allRelatedGames.filter(g =>
      !seriesGames.some(sg => sg.slug === g.slug) &&
      !priorityRelatedGames.some(pg => pg.slug === g.slug)
    );

    // PRIORITY 4: Get latest games as fallback
    const latestGames = getLatestGames(GAME_LIMITS.LATEST_GAMES_POOL)
      .filter((g) => g.slug !== game.slug && !usedSlugs.has(g.slug));

    // Build sidebar with priority order
    const sidebar: Game[] = [];
    const finalUsedSlugs = new Set<string>([game.slug]);

    // Add series games first (highest priority for related games)
    for (const seriesGame of seriesGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(seriesGame.slug)) {
        sidebar.push(seriesGame);
        finalUsedSlugs.add(seriesGame.slug);
      }
    }

    // Add priority games from same categories
    for (const priorityGame of priorityRelatedGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(priorityGame.slug)) {
        sidebar.push(priorityGame);
        finalUsedSlugs.add(priorityGame.slug);
      }
    }

    // Add remaining category games
    for (const categoryGame of remainingCategoryGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(categoryGame.slug)) {
        sidebar.push(categoryGame);
        finalUsedSlugs.add(categoryGame.slug);
      }
    }

    // Fill up with latest games if needed
    for (const latestGame of latestGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(latestGame.slug)) {
        sidebar.push(latestGame);
        finalUsedSlugs.add(latestGame.slug);
      }
    }

    // Build more games section
    const more: Game[] = [];

    // Add remaining series games that didn't fit in sidebar
    for (const seriesGame of seriesGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(seriesGame.slug)) {
        more.push(seriesGame);
        finalUsedSlugs.add(seriesGame.slug);
      }
    }

    // Add remaining priority games
    for (const priorityGame of priorityRelatedGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(priorityGame.slug)) {
        more.push(priorityGame);
        finalUsedSlugs.add(priorityGame.slug);
      }
    }

    // Add remaining category games
    for (const categoryGame of remainingCategoryGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(categoryGame.slug)) {
        more.push(categoryGame);
        finalUsedSlugs.add(categoryGame.slug);
      }
    }

    // Fill up with latest games if needed
    for (const latestGame of latestGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(latestGame.slug)) {
        more.push(latestGame);
        finalUsedSlugs.add(latestGame.slug);
      }
    }

    return { sidebarGames: sidebar, moreGames: more };
  }, [game]);
}
